import produce from 'immer'
import Actions from '../actions'
import _ from 'lodash'
import { serializePlanMembers } from '../utils/serializerUtil'
import { filterActiveMembersWithPermissions } from '../utils/familyPermissionsUtils'
import { getMemberColor } from '../utils/familyUtils'
import dayjs from 'dayjs'

const emptyMember = {
  firstName: null,
  lastName: null,
  dateOfBirth: null,
  sex: null,
  relation: null,
  email: null,
  phoneNumber: null,
  status: 'pending',
  permissions: {
    requested_permissions: [
      { desc: 'view_medical_records', status: null },
      { desc: 'participate_in_doctor_visit', status: null },
      { desc: 'create_doctor_visit', status: null }
    ]
  },
  urlParams: null
}

const initialState = {
  members: [],
  activeMembers: [],
  membersAcrossPolicies: [],
  owner: null,
  newMember: { ...emptyMember },
  loadingNewMember: false,
  error_fields: {},
  selectedMember: null,
  loadingDeleteMember: false,
  personalDoctor: null,
  optionalPersonalDoctor: null,
  navigatedFromChat: false,
  accountData: {},
  askNicelySlug: null,
  permissions: [],
  profileMemberId: null,
  clinicsData: null,
  userHomeAddressState: 'OH'
}

const updateActiveMembers = members => {
  // each time the member list changes, we update the active members and adding the color to the member
  if (!!members?.length) {
    const active = members.filter(filterActiveMembersWithPermissions)
    const sortedActiveMembersById = _.sortBy(active, [
      member => parseInt(member?.id)
    ])
    return sortedActiveMembersById?.map((member, i) => {
      return {
        ...member,
        color: getMemberColor(i)
      }
    })
  }
  return []
}

export default produce((draft, action) => {
  const { payload } = action

  switch (action.type) {
    case Actions.OPERATION_FAILED: {
      draft.loadingNewMember = false
      break
    }
    case Actions.ADD_NEW_MEMBER_REQUESTED: {
      draft.loadingNewMember = true
      break
    }
    case Actions.DETACH_MEMBER_REQUESTED: {
      draft.loadingDeleteMember = true
      break
    }
    case Actions.CHOOSE_FAMILY_MEMBER: {
      const { member } = payload
      draft.selectedMember = member
      break
    }
    case Actions.SHOW_REQUEST_MEMBER_PERMISSIONS:
    case Actions.SHOW_MEMBER: {
      const { id } = payload
      draft.profileMemberId = id
      break
    }
    case Actions.UPDATE_SEX_REQUESTED: {
      const { sex } = payload
      if (draft.selectedMember) {
        draft.selectedMember.sex = sex
      }
      break
    }
    case Actions.ADD_NEW_MEMBER_RECEIVED: {
      draft.members.push(payload)
      draft.newMember = { ...emptyMember }
      draft.loadingNewMember = false
      draft.activeMembers = updateActiveMembers(draft.members)
      break
    }
    case Actions.ADD_NEW_MEMBER_ERRORS: {
      draft.loadingNewMember = false
      const { error_fields } = payload
      draft.error_fields = error_fields
      break
    }
    case Actions.CLEAR_ADD_NEW_MEMBER_ERROR: {
      draft.error_fields = {}
      break
    }
    case Actions.DETACH_MEMBER_RECEIVED: {
      const { id } = payload
      const index = draft.members
        .map(x => {
          return x.id
        })
        .indexOf(id)
      if (index > -1) {
        draft.members.splice(index, 1)
      }
      draft.loadingDeleteMember = false
      draft.activeMembers = updateActiveMembers(draft.members)
      break
    }
    case Actions.ADD_NEW_MEMBER_DETAILS: {
      draft.newMember = { ...draft.newMember, ...payload }
      break
    }
    case Actions.ADD_PERMISSIONS_NEW_MEMBER: {
      const { permission } = payload
      draft.newMember.permissions.requested_permissions.forEach(element => {
        if (element.desc === permission.desc) element.status = 'requested'
      })

      break
    }
    case Actions.USER_PROFILE_RECEIVED: {
      const { profile } = payload
      const members = serializePlanMembers(profile.account?.members)
      const membersAcrossPolicies = serializePlanMembers(
        profile.account?.membersAcrossPolicies
      )
      draft.permissions = profile.account?.permissions
      draft.owner = profile.account?.owner
      draft.members = members
      draft.membersAcrossPolicies = membersAcrossPolicies || []
      draft.personalDoctor = profile.account?.personalDoctor
      draft.activeMembers = updateActiveMembers(members)
      draft.userHomeAddressState =
        profile?.address?.find(
          address => address?.address_type === 'Residential'
        )?.state ||
        profile?.home_state ||
        profile?.billing_state
      break
    }
    case Actions.CHOOSE_PERSONAL_DOCTOR_RECEIVED: {
      const { doctor } = payload

      draft.personalDoctor = {
        personalDoctor: doctor,
        personalDoctorSelected: true
      }

      break
    }
    case Actions.PERSONAL_DOCTOR_DETAILS_RECEIVED: {
      const { doctor } = payload
      draft.optionalPersonalDoctor = doctor
      break
    }
    case Actions.CLINIC_DETAILS_REQUESTED: {
      const { clinic } = payload
      draft.selectedClinic = clinic
      break
    }
    case Actions.UPDATE_GRANT_PERMISSION_NO_TOGGLE_REQUESTED: {
      const newPermission = payload
      draft.members = draft.members.map(member => {
        if (+member.id === newPermission.subject) {
          member.permissions.granted_permissions =
            member.permissions.granted_permissions.map(perm => {
              if (perm.desc === newPermission.permission_type) {
                const status = newPermission.status
                return {
                  ...perm,
                  status,
                  isLoading: true
                }
              }
              return perm
            })
        }
        return member
      })
      draft.activeMembers = updateActiveMembers(draft.members)
      break
    }
    case Actions.UPDATE_GRANT_PERMISSION_REQUESTED: {
      const newPermission = payload
      draft.members = draft.members.map(member => {
        if (+member.id === newPermission.subject) {
          member.permissions.granted_permissions =
            member.permissions.granted_permissions.map(perm => {
              if (perm.desc === newPermission.permission_type) {
                const status =
                  newPermission.status === 'approved' ? 'denied' : 'approved'
                return {
                  ...perm,
                  status,
                  isLoading: true
                }
              }
              return perm
            })
        }
        return member
      })
      draft.activeMembers = updateActiveMembers(draft.members)
      break
    }
    case Actions.UPDATE_GRANT_PERMISSION_RECEIVED: {
      const newPermission = payload
      draft.members = draft.members.map(member => {
        if (+member.id === newPermission.subject_patient) {
          member.permissions.granted_permissions =
            member.permissions.granted_permissions.map(perm => {
              if (perm.desc === newPermission.permission_type) {
                return {
                  ...perm,
                  status: newPermission.status,
                  isLoading: false
                }
              }
              return perm
            })
        }
        return member
      })
      draft.activeMembers = updateActiveMembers(draft.members)
      break
    }
    case Actions.REQUEST_PERMISSION_REQUESTED: {
      const { permission } = payload
      draft.members = draft.members.map(member => {
        if (member.id === draft.selectedMember?.id) {
          member.permissions.granted_permissions =
            member.permissions.granted_permissions.map(perm => {
              if (perm.desc === permission.desc) {
                return { ...permission, isLoading: true }
              }
              return perm
            })
        }
        return member
      })
      draft.activeMembers = updateActiveMembers(draft.members)
      break
    }
    case Actions.REQUEST_PERMISSION_RECEIVED: {
      const { permission } = payload
      draft.members = draft.members.map(member => {
        if (member.id === draft.profileMemberId) {
          member.permissions.granted_permissions =
            member.permissions.granted_permissions.map(perm => {
              if (perm.desc === permission.desc) {
                return { ...permission, isLoading: false, status: 'requested' }
              }
              return perm
            })
        }
        return member
      })
      draft.activeMembers = updateActiveMembers(draft.members)
      break
    }
    case Actions.NAVIGATION_FROM_CHAT_STARTED: {
      draft.navigatedFromChat = true
      break
    }
    case Actions.NAVIGATION_FROM_CHAT_FINISHED: {
      draft.navigatedFromChat = false
      break
    }
    case Actions.ACCOUNT_DATA_RECEIVED: {
      draft.accountData = payload
      break
    }
    case Actions.CLINICS_DATA_RECEIVED: {
      draft.clinicsData = payload
      break
    }
    case Actions.LAST_LOCATION_RECEIVED: {
      draft.lastLocation = payload
      break
    }
    case Actions.ASK_NICELY_SLUG_RECEIVED: {
      draft.askNicelySlug = payload?.slug
      break
    }
    case Actions.IS_SIGNED_IN_RECEIVED: {
      draft.selectedMember = null
      break
    }
    case Actions.SIGNOUT_RECEIVED: {
      return initialState
    }
    case Actions.UPDATE_EMERGENCY_CONTACT_RECEIVED: {
      const { contactName, phoneNumber, patientId } = payload
      if (patientId === draft.selectedMember?.id) {
        draft.selectedMember = {
          ...draft.selectedMember,
          emergencyContact: { phoneNumber, contactName }
        }
      }
      draft.activeMembers = draft.activeMembers.map(member => {
        if (member.id === patientId) {
          member.emergencyContact = { phoneNumber, contactName }
        }
        return member
      })
      break
    }
    case Actions.UPDATE_MY_PHARMACY_RECEIVED: {
      const { pharmacy, patientId } = payload
      if (patientId === draft.selectedMember?.id) {
        draft.selectedMember = {
          ...draft.selectedMember,
          pharmacy
        }
      }
      draft.activeMembers = draft.activeMembers.map(member => {
        if (member.id === patientId) {
          member.pharmacy = pharmacy
        }
        return member
      })
      break
    }
    case Actions.INVITE_HMO_MEMBER_TO_DOWNLOAD_APP_RECEIVED: {
      const { patientId } = payload
      draft.members = draft.members.map(member => {
        if (member.id === patientId) {
          member.app_link_invitation_date = dayjs.utc()
        }
        return member
      })
      break
    }
    default: {
      // Nothing to do
      break
    }
  }
}, initialState)
